import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "../../../api/axios";
import toast from "react-hot-toast";
import { RightDrawer } from "../../utils/PMTableView/RightDrawer";
import { ConfirmDialog } from "../../utils/Prompt";
import { DrawingPdfUploadField } from "./drawing-pdf-upload-field";
import {
  createDrawingFloor,
  fetchDrawingHouses,
  findFloorInStructure,
} from "./drawingStructureApi";
import {
  DEFAULT_DRAWING_KIND,
  DRAWING_KINDS,
  drawingKindLabel,
} from "./drawingKinds";
import { drawingKindStyle } from "./drawingKindStyles";

const isValidDrawingId = (value) => {
  if (value == null || value === "") return false;
  const asString = String(value);
  return asString !== "undefined" && asString !== "null" && /^\d+$/.test(asString);
};

const normalizeDrawingKind = (kind) => {
  const normalized = typeof kind === "string" ? kind.trim() : kind;
  return normalized ? normalized : DEFAULT_DRAWING_KIND;
};

/**
 * Right-side create drawer for drawings (list redesign).
 * Prefills project + floor from the current list selection.
 */
export const DrawingCreateDrawer = ({
  t,
  open,
  onClose,
  project,
  initialFloorId = null,
  initialHouseId = null,
  initialAndre = false,
  onCreated,
}) => {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [houses, setHouses] = useState([]);
  const [title, setTitle] = useState("");
  const [drawingKind, setDrawingKind] = useState(DEFAULT_DRAWING_KIND);
  const [drawingFloorId, setDrawingFloorId] = useState(null);
  const [selectedHouseId, setSelectedHouseId] = useState(null);
  const [selectedAndre, setSelectedAndre] = useState(false);
  const [uploadedDocument, setUploadedDocument] = useState(null);
  const [replaceDialog, setReplaceDialog] = useState(null);
  const [addingFloorForHouseId, setAddingFloorForHouseId] = useState(null);
  const [floorDraft, setFloorDraft] = useState("");

  useEffect(() => {
    if (!open) return;
    setTitle("");
    setDrawingKind(DEFAULT_DRAWING_KIND);
    setUploadedDocument(null);
    setReplaceDialog(null);
    setFloorDraft("");
    setAddingFloorForHouseId(null);

    if (initialAndre) {
      setSelectedAndre(true);
      setDrawingFloorId(null);
      setSelectedHouseId(null);
    } else if (initialFloorId) {
      setSelectedAndre(false);
      setDrawingFloorId(initialFloorId);
      setSelectedHouseId(null);
    } else if (initialHouseId) {
      setSelectedAndre(false);
      setDrawingFloorId(null);
      setSelectedHouseId(initialHouseId);
    } else {
      setSelectedAndre(true);
      setDrawingFloorId(null);
      setSelectedHouseId(null);
    }
  }, [open, initialFloorId, initialHouseId, initialAndre]);

  useEffect(() => {
    if (!open || !project?.id) {
      setHouses([]);
      return;
    }
    let cancelled = false;
    fetchDrawingHouses(project.id)
      .then((data) => {
        if (!cancelled) setHouses(data);
      })
      .catch(() => {
        if (!cancelled) setHouses([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open, project?.id]);

  useEffect(() => {
    if (!drawingFloorId || !houses.length) return;
    const match = findFloorInStructure(houses, drawingFloorId);
    if (match?.house?.id) setSelectedHouseId(match.house.id);
  }, [drawingFloorId, houses]);

  const floorsForHouse = useMemo(() => {
    if (!selectedHouseId) return [];
    const house = houses.find((h) => h.id === selectedHouseId);
    return house?.floors || [];
  }, [houses, selectedHouseId]);

  const panelContext = useMemo(() => {
    if (!project?.displayTitle) return t("drawings.create_drawer.context_fallback");
    if (selectedAndre) {
      return `${project.displayTitle} · ${t("drawings.structure.andre")}`;
    }
    const match = drawingFloorId
      ? findFloorInStructure(houses, drawingFloorId)
      : null;
    if (match) {
      return `${project.displayTitle} · ${match.house.name} · ${t("drawings.floor")} ${match.floor.name}`;
    }
    if (selectedHouseId) {
      const house = houses.find((h) => h.id === selectedHouseId);
      if (house) return `${project.displayTitle} · ${house.name}`;
    }
    return project.displayTitle;
  }, [
    project,
    selectedAndre,
    drawingFloorId,
    selectedHouseId,
    houses,
    t,
  ]);

  const findExistingDrawingForFloorAndKind = async (floorId, kind) => {
    if (!floorId) return null;
    const targetKind = normalizeDrawingKind(kind);
    const response = await axios({
      method: "get",
      url: "/drawings/",
      params: {
        drawing_floor_id: floorId,
        per_page: 5,
        page: 1,
        fields:
          "id,title,drawing_kind,floor,house,drawing_floor_id,drawing_floor,document",
      },
    });
    const rows = response.data?.data || [];
    return (
      rows.find((row) => {
        const rowKind = normalizeDrawingKind(row.drawing_kind);
        return (
          String(row.drawing_floor?.id || row.drawing_floor_id) ===
            String(floorId) && rowKind === targetKind
        );
      }) || null
    );
  };

  const persistDrawing = async ({ targetId, replacedExisting }) => {
    const payload = {
      drawing: {
        title: title.trim(),
        description: "",
        project_id: project?.id || null,
        drawing_floor_id: drawingFloorId || null,
        drawing_kind: drawingKind || DEFAULT_DRAWING_KIND,
        document_id: uploadedDocument?.id || null,
      },
    };

    let response;
    if (isValidDrawingId(targetId)) {
      response = await axios({
        method: "put",
        url: `/drawings/${targetId}`,
        data: payload,
      });
      toast.success(
        replacedExisting ? t("drawings.replaced") : t("drawings.updated"),
      );
    } else {
      response = await axios({
        method: "post",
        url: "/drawings/",
        data: payload,
      });
      toast.success(t("drawings.created"));
    }

    const savedId = response.data?.id;
    if (!isValidDrawingId(savedId)) {
      toast.error(t("drawings.create_error"));
      return null;
    }
    return savedId;
  };

  const resetFormForAnother = () => {
    setTitle("");
    setUploadedDocument(null);
    setReplaceDialog(null);
    setDrawingKind(DEFAULT_DRAWING_KIND);
  };

  const finishCreate = async (savedId, andAnother) => {
    onCreated?.(savedId);
    if (andAnother) {
      resetFormForAnother();
      return;
    }
    onClose?.();
    navigate(`/dashboard/drawings/${savedId}`);
  };

  const handleSubmit = async ({ andAnother = false } = {}) => {
    if (!title.trim()) {
      toast.error(t("drawings.title.required"));
      return;
    }
    if (!uploadedDocument?.id) {
      toast.error(t("drawings.document.required"));
      return;
    }
    if (!project?.id) {
      toast.error(t("drawings.select_project"));
      return;
    }
    if (!selectedAndre && !drawingFloorId) {
      toast.error(t("drawings.create_drawer.floor_required"));
      return;
    }

    try {
      setSaving(true);

      if (drawingFloorId) {
        const existing = await findExistingDrawingForFloorAndKind(
          drawingFloorId,
          drawingKind,
        );
        if (isValidDrawingId(existing?.id)) {
          const house =
            existing?.drawing_floor?.drawing_house?.name ||
            existing?.house ||
            "—";
          const floor =
            existing?.drawing_floor?.name || existing?.floor || "—";
          const kindLabel = drawingKindLabel(
            t,
            existing?.drawing_kind || drawingKind,
          );
          setReplaceDialog({
            existing,
            andAnother,
            message: t("drawings.replace_confirm.message", {
              title: existing?.title || `#${existing?.id}`,
              house,
              floor,
              kind: kindLabel,
            }),
          });
          return;
        }
      }

      const savedId = await persistDrawing({
        targetId: null,
        replacedExisting: false,
      });
      if (savedId) await finishCreate(savedId, andAnother);
    } catch (err) {
      console.error("Error creating drawing:", err);
      if (err?.response?.status === 409 && err?.response?.data?.conflict) {
        const conflict = err.response.data.conflict;
        const existingDrawingId = conflict.existing_drawing_id;
        if (!isValidDrawingId(existingDrawingId)) {
          toast.error(t("drawings.floor_kind_conflict"));
          return;
        }
        setReplaceDialog({
          existing: {
            id: existingDrawingId,
            title: conflict.title,
            house: conflict.house,
            floor: conflict.floor,
            drawing_kind: conflict.drawing_kind,
            drawing_floor: {
              id: conflict.drawing_floor_id,
              name: conflict.floor,
              drawing_house: { name: conflict.house },
            },
          },
          andAnother,
          message: t("drawings.replace_confirm.message", {
            title: conflict.title || `#${existingDrawingId}`,
            house: conflict.house || "—",
            floor: conflict.floor || "—",
            kind: drawingKindLabel(t, conflict.drawing_kind || drawingKind),
          }),
        });
        return;
      }
      const apiErrors = err?.response?.data?.errors;
      if (Array.isArray(apiErrors) && apiErrors.length > 0) {
        toast.error(apiErrors.join(", "));
      } else {
        toast.error(t("drawings.create_error"));
      }
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmReplace = async () => {
    const existing = replaceDialog?.existing;
    const andAnother = Boolean(replaceDialog?.andAnother);
    setReplaceDialog(null);
    if (!isValidDrawingId(existing?.id)) {
      toast.error(t("drawings.replace_confirm.invalid_target"));
      return;
    }
    try {
      setSaving(true);
      const savedId = await persistDrawing({
        targetId: existing.id,
        replacedExisting: true,
      });
      if (savedId) await finishCreate(savedId, andAnother);
    } catch (err) {
      console.error("Error replacing drawing:", err);
      toast.error(t("drawings.update_error"));
    } finally {
      setSaving(false);
    }
  };

  const handleAddFloor = async () => {
    const name = floorDraft.trim();
    if (!selectedHouseId || !name) return;
    try {
      const created = await createDrawingFloor(selectedHouseId, name);
      setHouses((current) =>
        current.map((house) =>
          house.id === selectedHouseId
            ? {
                ...house,
                floors: [
                  ...(house.floors || []),
                  { ...created, drawings_count: 0 },
                ],
              }
            : house,
        ),
      );
      setDrawingFloorId(created.id);
      setSelectedAndre(false);
      setFloorDraft("");
      setAddingFloorForHouseId(null);
      toast.success(t("drawings.structure.floor_created"));
    } catch (err) {
      console.error("Error creating floor:", err);
      toast.error(t("drawings.structure.save_error"));
    }
  };

  return (
    <>
      <RightDrawer
        open={open}
        onClose={onClose}
        title={t("drawings.new")}
        eyebrow={panelContext}
        width={492}
        footer={
          <div className="flex w-full min-w-[320px] flex-wrap items-center gap-2">
            <button
              type="button"
              disabled={saving}
              onClick={() => handleSubmit({ andAnother: false })}
              className="rounded-lg bg-[#1F4BE0] px-4 py-2.5 text-[13.5px] font-medium text-white hover:bg-[#163AB4] disabled:opacity-50"
            >
              {saving ? t("Saving...") : t("drawings.create_drawer.create")}
            </button>
            <button
              type="button"
              disabled={saving}
              onClick={() => handleSubmit({ andAnother: true })}
              className="rounded-lg border border-[#D8DDE4] bg-white px-4 py-2.5 text-[13.5px] font-medium text-[#37414F] hover:bg-[#F5F7FA] disabled:opacity-50"
            >
              {t("drawings.create_drawer.create_another")}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="ml-auto px-2 py-2.5 text-[13.5px] text-[#6B7482] hover:text-[#12161C]"
            >
              {t("Cancel")}
            </button>
          </div>
        }
      >
        <div className="flex flex-col gap-5">
          <div>
            <div className="mb-1.5 text-[12px] font-semibold text-[#37414F]">
              {t("drawings.document")}
            </div>
            {uploadedDocument ? (
              <div className="rounded-[10px] border border-[#E4E7EC] bg-[#F8FAFD] p-4">
                <p className="text-[13px] font-medium text-[#12161C]">
                  {uploadedDocument.title || t("drawings.document")}
                </p>
                <button
                  type="button"
                  onClick={() => setUploadedDocument(null)}
                  className="mt-2 text-[12px] text-red-600 hover:text-red-800"
                >
                  {t("drawings.document.replace")}
                </button>
              </div>
            ) : (
              <DrawingPdfUploadField
                t={t}
                title={title}
                disabled={saving}
                onUploaded={(document) => {
                  if (!document?.id) return;
                  setUploadedDocument(document);
                  if (!title.trim() && document.title) {
                    setTitle(String(document.title).replace(/\.pdf$/i, ""));
                  }
                }}
              />
            )}
            <p className="mt-2 text-[12px] text-[#6B7482]">
              {t("drawings.create_drawer.pdf_hint")}
            </p>
          </div>

          <div>
            <div className="mb-1.5 text-[12px] font-semibold text-[#37414F]">
              {t("drawings.title")}
            </div>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t("drawings.create_drawer.title_placeholder")}
              className="w-full rounded-lg border border-[#D8DDE4] px-3 py-2.5 text-[13.5px] outline-none focus:border-[#1F4BE0]"
            />
          </div>

          <div>
            <div className="mb-1.5 text-[12px] font-semibold text-[#37414F]">
              {t("drawings.kind")}
            </div>
            <div className="flex flex-wrap gap-2">
              {DRAWING_KINDS.map((kind) => {
                const active = drawingKind === kind;
                const style = drawingKindStyle(kind);
                return (
                  <button
                    key={kind}
                    type="button"
                    onClick={() => {
                      setDrawingKind(kind);
                      setReplaceDialog(null);
                    }}
                    className="relative rounded-full border px-3.5 py-1.5 text-[13px]"
                    style={{
                      borderColor: active ? style.color : "#D8DDE4",
                      background: active ? style.bg : "#fff",
                      color: active ? style.color : "#37414F",
                      fontWeight: active ? 600 : 500,
                    }}
                  >
                    {drawingKindLabel(t, kind)}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <div className="mb-1.5 flex items-baseline justify-between">
              <div className="text-[12px] font-semibold text-[#37414F]">
                {t("drawings.create_drawer.location")}
              </div>
              <div className="text-[11.5px] text-[#8C95A3]">
                {t("drawings.create_drawer.location_hint")}
              </div>
            </div>
            <div className="overflow-hidden rounded-[10px] border border-[#E4E7EC]">
              <div className="flex flex-wrap gap-1.5 border-b border-[#E4E7EC] bg-[#F8FAFD] p-2.5">
                <button
                  type="button"
                  onClick={() => {
                    setSelectedAndre(true);
                    setDrawingFloorId(null);
                    setSelectedHouseId(null);
                    setReplaceDialog(null);
                  }}
                  className={`rounded-md border px-2.5 py-1.5 text-[12.5px] ${
                    selectedAndre
                      ? "border-[#12161C] bg-[#12161C] text-white"
                      : "border-[#DCE0E6] bg-white text-[#37414F] hover:border-[#1F4BE0]"
                  }`}
                >
                  {t("drawings.structure.andre")}
                </button>
                {houses.map((house) => {
                  const active =
                    !selectedAndre && selectedHouseId === house.id;
                  return (
                    <button
                      key={house.id}
                      type="button"
                      onClick={() => {
                        setSelectedAndre(false);
                        setSelectedHouseId(house.id);
                        const firstFloor = house.floors?.[0];
                        setDrawingFloorId(firstFloor?.id || null);
                        setReplaceDialog(null);
                      }}
                      className={`rounded-md border px-2.5 py-1.5 text-[12.5px] ${
                        active
                          ? "border-[#12161C] bg-[#12161C] text-white"
                          : "border-[#DCE0E6] bg-white text-[#37414F] hover:border-[#1F4BE0]"
                      }`}
                    >
                      {house.name}
                    </button>
                  );
                })}
              </div>
              {!selectedAndre && selectedHouseId ? (
                <div className="flex flex-wrap gap-1.5 p-2.5">
                  {floorsForHouse.map((floor) => {
                    const active = drawingFloorId === floor.id;
                    return (
                      <button
                        key={floor.id}
                        type="button"
                        onClick={() => {
                          setDrawingFloorId(floor.id);
                          setReplaceDialog(null);
                        }}
                        className={`rounded-md border px-2.5 py-1.5 text-[12.5px] ${
                          active
                            ? "border-[#1F4BE0] bg-[#F3F6FF] text-[#1F4BE0]"
                            : "border-[#DCE0E6] text-[#37414F] hover:border-[#1F4BE0]"
                        }`}
                      >
                        {t("drawings.floor")} {floor.name}
                      </button>
                    );
                  })}
                  {addingFloorForHouseId === selectedHouseId ? (
                    <div className="flex w-full gap-2 pt-1">
                      <input
                        type="text"
                        value={floorDraft}
                        onChange={(e) => setFloorDraft(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            handleAddFloor();
                          }
                        }}
                        placeholder={t(
                          "drawings.structure.new_floor_placeholder",
                        )}
                        className="min-w-0 flex-1 rounded-md border border-[#C9D6FA] px-2.5 py-1.5 text-[12.5px] outline-none"
                        autoFocus
                      />
                      <button
                        type="button"
                        onClick={handleAddFloor}
                        className="rounded-md bg-[#1F4BE0] px-2.5 py-1.5 text-[12px] font-medium text-white"
                      >
                        {t("drawings.structure.add_floor")}
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setAddingFloorForHouseId(selectedHouseId)}
                      className="rounded-md border border-dashed border-[#C9D0DA] px-2.5 py-1.5 text-[12.5px] text-[#79828F] hover:border-[#1F4BE0] hover:text-[#1F4BE0]"
                    >
                      + {t("drawings.floor")}
                    </button>
                  )}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </RightDrawer>

      <ConfirmDialog
        open={Boolean(replaceDialog)}
        onClose={() => setReplaceDialog(null)}
        onConfirm={handleConfirmReplace}
        title={t("drawings.replace_confirm.title")}
        message={replaceDialog?.message || ""}
        confirmText={t("drawings.replace_confirm.confirm")}
        cancelText={t("drawings.replace_confirm.cancel")}
      />
    </>
  );
};
